import React, { useState, useEffect } from 'react';
import { amDesignColors, amDesignTypography, amDesignComponents } from '../../themes/amDesignSystem';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Stack,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const SettingsPage = () => {
  const { user } = useAuth();
  const [subUsers, setSubUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'member'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Load sub-users
  useEffect(() => {
    if (user?.userType === 'user') {
      loadSubUsers();
    }
  }, [user]);

  const loadSubUsers = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get('/api/team/sub-users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubUsers(response.data.subUsers || []);
      setError('');
    } catch (err) {
      setError('Fehler beim Laden der Benutzer');
      console.error('Error loading sub-users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        role: user.role
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'member'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'member'
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('auth_token');
      
      if (editingUser) {
        // Update existing user
        await axios.put(`/api/team/sub-users/${editingUser._id}`, {
          name: formData.name,
          role: formData.role
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Create new user
        if (!formData.password) {
          setError('Passwort ist erforderlich');
          return;
        }
        
        await axios.post('/api/team/sub-users', formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      handleCloseDialog();
      await loadSubUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Möchten Sie diesen Benutzer wirklich löschen?')) return;

    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`/api/team/sub-users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await loadSubUsers();
    } catch (err) {
      setError('Fehler beim Löschen des Benutzers');
    }
  };

  const handleChangePassword = async (userId) => {
    const newPassword = prompt('Neues Passwort eingeben:');
    if (!newPassword) return;

    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(`/api/team/sub-users/${userId}/change-password`, {
        newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Passwort erfolgreich geändert');
    } catch (err) {
      setError('Fehler beim Ändern des Passworts');
    }
  };

  if (user?.userType === 'subuser') {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Einstellungen
        </Typography>
        
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Benutzer-Informationen
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography><strong>Name:</strong> {user.name}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography><strong>E-Mail:</strong> {user.email}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography><strong>Rolle:</strong> {user.role}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography><strong>Benutzer-Typ:</strong> Team-Mitglied</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Einstellungen
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Team-Mitglieder
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog()}
                  sx={{ bgcolor: '#C41E3A', '&:hover': { bgcolor: '#A01729' } }}
                >
                  Benutzer hinzufügen
                </Button>
              </Box>
              
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              
              {loading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>E-Mail</TableCell>
                        <TableCell>Rolle</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Erstellt</TableCell>
                        <TableCell>Aktionen</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {subUsers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography color="textSecondary">
                              Noch keine Team-Mitglieder vorhanden
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        subUsers.map((subUser) => (
                          <TableRow key={subUser._id}>
                            <TableCell>
                              <Box display="flex" alignItems="center">
                                <PersonIcon sx={{ mr: 1, color: amDesignColors.text.secondary }} />
                                {subUser.name}
                              </Box>
                            </TableCell>
                            <TableCell>{subUser.email}</TableCell>
                            <TableCell>
                              <Chip 
                                label={subUser.role} 
                                color={subUser.role === 'admin' ? 'primary' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={subUser.status} 
                                color={subUser.status === 'active' ? 'success' : 'warning'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {new Date(subUser.createdAt).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog(subUser)}
                                title="Bearbeiten"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleChangePassword(subUser._id)}
                                title="Passwort ändern"
                              >
                                <LockIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(subUser._id)}
                                title="Löschen"
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Team-Mitglieder können sich mit E-Mail und Passwort anmelden und haben Zugriff auf dieselben Unternehmensdaten wie der Hauptbenutzer.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add/Edit User Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? 'Benutzer bearbeiten' : 'Neuen Benutzer hinzufügen'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              label="E-Mail"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required={!editingUser}
              disabled={editingUser}
              sx={{ mb: 2 }}
            />
            
            {!editingUser && (
              <TextField
                fullWidth
                label="Passwort"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  )
                }}
              />
            )}
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Rolle</InputLabel>
              <Select
                value={formData.role}
                label="Rolle"
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              >
                <MenuItem value="member">Mitglied</MenuItem>
                <MenuItem value="admin">Administrator</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Abbrechen
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            sx={{ bgcolor: '#C41E3A', '&:hover': { bgcolor: '#A01729' } }}
          >
            {submitting ? <CircularProgress size={24} /> : (editingUser ? 'Speichern' : 'Hinzufügen')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsPage;